import { useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Mail, Printer, Share2, Loader2, Receipt, Forward, X } from "lucide-react";
import { toast } from "sonner";

/**
 * In-browser receipt preview modal.
 *
 * Replaces the old auto-download flow: when the patient clicks the receipt
 * icon, we fetch the PDF as a blob, render it in an <iframe>, and surface
 * Download / Print / Email-me-a-copy / Share actions on the same screen.
 *
 * Props:
 *   open       - bool, whether the dialog is open
 *   onClose    - () => void, called when user dismisses
 *   sessionId  - Stripe session id of the receipt to preview (required when open)
 *   receiptId  - short receipt id used for filenames + the title bar
 *   tierLabel  - optional short label ("Monthly subscription", etc.)
 */
export default function ReceiptPreviewDialog({ open, onClose, sessionId, receiptId, tierLabel }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardTo, setForwardTo] = useState("");
  const [forwarding, setForwarding] = useState(false);
  const blobRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !sessionId) return;
      setLoading(true);
      try {
        const res = await API.get(`/receipts/${encodeURIComponent(sessionId)}.pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        blobRef.current = url;
        setBlobUrl(url);
      } catch {
        toast.error("Couldn't load the receipt — please try again.");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, onClose]);

  // Revoke the blob URL when the dialog actually closes (not on every prop change)
  useEffect(() => {
    if (!open && blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = "";
      setBlobUrl("");
    }
  }, [open]);

  // Reset the forward form whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setForwardOpen(false);
      setForwardTo("");
    }
  }, [open]);

  const filename = `GLPFlo-receipt-${receiptId || "receipt"}.pdf`;

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    // Open the PDF in a new window and trigger native print dialog. Works on
    // desktop browsers; on mobile it falls back to opening the PDF in a tab
    // where the user can use the system share/print sheet.
    const w = window.open(blobUrl, "_blank");
    if (!w) {
      toast.error("Pop-up blocked — please allow pop-ups to print.");
      return;
    }
    // Some browsers fire print before the PDF renders; small delay helps.
    try {
      w.addEventListener("load", () => {
        try { w.focus(); w.print(); } catch { /* user can use the toolbar */ }
      });
    } catch { /* cross-origin restriction in some embedded PDF viewers */ }
  };

  const handleEmail = async () => {
    if (!sessionId) return;
    setEmailing(true);
    try {
      const { data } = await API.post(`/receipts/${encodeURIComponent(sessionId)}/email`);
      toast.success(`Receipt emailed to ${data.to_email || "your inbox"}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send the email — please try again.";
      toast.error(msg);
    } finally {
      setEmailing(false);
    }
  };

  const handleForward = async () => {
    const to = forwardTo.trim();
    if (!to) return;
    if (!sessionId) return;
    setForwarding(true);
    try {
      const { data } = await API.post(
        `/receipts/${encodeURIComponent(sessionId)}/email`,
        { to_email: to },
      );
      toast.success(`Receipt forwarded to ${data.to_email || to}.`);
      setForwardOpen(false);
      setForwardTo("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't forward the receipt — please try again.";
      toast.error(msg);
    } finally {
      setForwarding(false);
    }
  };

  const handleShare = async () => {
    if (!blobUrl) return;
    try {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      const file = new File([blob], filename, { type: "application/pdf" });
      // Web Share API Level 2 — supported on iOS Safari, Android Chrome, etc.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "GLPFlo Order Receipt",
          text: "My GLPFlo order receipt",
        });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return; // user cancelled
    }
    // Fallback: text-only share (no file)
    if (navigator.share) {
      try {
        await navigator.share({ title: "GLPFlo Order Receipt", text: `My GLPFlo receipt #${receiptId || ""}` });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    toast.info("Share isn't supported on this device — use Download instead.");
  };

  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl w-[95vw] p-0 overflow-hidden" data-testid="receipt-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0">
              <Receipt className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }} className="text-base">
                Order Receipt
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                {receiptId && <span className="font-mono">#{receiptId}</span>}
                {tierLabel && <span className="ml-2">· {tierLabel}</span>}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="bg-slate-50 h-[60vh] sm:h-[65vh] flex items-center justify-center" data-testid="receipt-preview-frame-wrap">
          {loading || !blobUrl ? (
            <div className="text-slate-400 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading receipt…
            </div>
          ) : (
            <iframe
              src={blobUrl}
              title="Order Receipt"
              className="w-full h-full bg-white"
              data-testid="receipt-preview-iframe"
            />
          )}
        </div>

        <div className="border-t bg-white" data-testid="receipt-preview-actions-wrap">
          {forwardOpen && (
            <div className="px-4 sm:px-6 py-3 bg-purple-50/40 border-b border-purple-100" data-testid="receipt-forward-form">
              <div className="flex items-center gap-2">
                <Forward className="w-4 h-4 text-purple-600 shrink-0" />
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={forwardTo}
                  onChange={(e) => setForwardTo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleForward(); }}
                  className="h-9 text-sm rounded-full"
                  autoFocus
                  data-testid="receipt-forward-input"
                />
                <Button
                  size="sm"
                  className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                  onClick={handleForward}
                  disabled={forwarding || !forwardTo.trim()}
                  data-testid="receipt-forward-send"
                >
                  {forwarding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
                  Send
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full shrink-0"
                  onClick={() => { setForwardOpen(false); setForwardTo(""); }}
                  data-testid="receipt-forward-cancel"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5 ml-6">A copy of this receipt will be sent to that address — useful for HSA/FSA reimbursement, accountants, or family.</p>
            </div>
          )}
          <div className="px-4 sm:px-6 py-3 flex flex-wrap gap-2 justify-end" data-testid="receipt-preview-actions">
            <Button variant="outline" size="sm" className="rounded-full" onClick={handlePrint} disabled={!blobUrl} data-testid="receipt-action-print">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={handleEmail} disabled={emailing || !sessionId} data-testid="receipt-action-email">
              {emailing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
              Email me a copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setForwardOpen(v => !v)}
              disabled={!sessionId}
              data-testid="receipt-action-forward"
            >
              <Forward className="w-4 h-4 mr-1.5" /> Forward to another email
            </Button>
            {canShare && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={handleShare} disabled={!blobUrl} data-testid="receipt-action-share">
                <Share2 className="w-4 h-4 mr-1.5" /> Share
              </Button>
            )}
            <Button size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" onClick={handleDownload} disabled={!blobUrl} data-testid="receipt-action-download">
              <Download className="w-4 h-4 mr-1.5" /> Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
